import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../actions'
import { assemblePlanKey, getRenewDate } from '../../utils/billingUtils'
import { divideByThousands } from '../../utils/stringUtils'
import CreditCardPaymentIcons from '../common/inputs/billing/CreditCardPaymentIcons'
import CreditCardPicker from '../common/inputs/billing/CreditCardPicker'
import BaseModal from '../common/modals/BaseModal'
import OrderItemsList from '../common/OrderItemsList'
import DazzedHeading16 from '../common/text/DazzedHeading16'
import DazzedParagraph12 from '../common/text/DazzedParagraph12'
import { useCountry } from '../../hooks/useCountry'
import useGaFlags from '../../hooks/useGaFlags'
import useScopesShippingFee from '../../hooks/useScopesShippingFee'
import { BILLING_PERIOD_ANNUALLY, BILLING_PERIOD_MONTHLY } from '../../consts/grinPlanConsts'
import { COUNTRY_US, COUNTRY_IN } from '../../consts/countryConsts'

const useStyles = makeStyles({
  body: {
    top: 40,
    minWidth: window.innerWidth,
    height: window.innerHeight - 80,
    borderRadius: 0
  },
  contentContainer: {
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    width: '50%',
    alignSelf: 'center'
  },
  typeText: {
    marginBottom: 8,
    fontWeight: 600
  },
  calculationItemNote: {
    marginLeft: 15,
    marginTop: 10,
    marginBottom: 10,
    fontSize: 14
  },
  horizontalDivider: {
    marginTop: 30,
    marginBottom: 30,
    height: 1,
    backgroundColor: 'var(--text-color-12)'
  },
  creditCardContainer: {
    width: 540,
    marginBottom: 20
  },
  creditCardTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  creditCardTitle: {
    marginBottom: 20
  },
  shippingAddressTitle: {
    marginTop: 30,
    marginBottom: 20
  },
  termsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 20
  },
  link: {
    fontWeight: 500,
    color: 'var(--text-color-3)',
    cursor: 'pointer'
  }
})

const BillingUpgradePlanDialog = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isOpen = useSelector(state => state.billingReducer.upgradePlan.isUpgradePlanDialogOpen)
  const selectedPlanGroup = useSelector(state => state.billingReducer.upgradePlan.selectedPlanGroup)
  const selectedPlanPeriod = useSelector(state => state.billingReducer.upgradePlan.selectedPlanPeriod)
  const isSelectedPlanUpgrading = useSelector(state => state.billingReducer.upgradePlan.isLoading)
  const grinPlans = useSelector(state => state.billingReducer.grinPlans)
  const practiceDetails = useSelector(state => state.practiceReducer.details)
  const [shippingAddress, setShippingAddress] = useState({})

  const scopesQuantity = useSelector(state => state.billingReducer.upgradePlan.selectedPlanScopesQuantity)
  const scopesMiniQuantity = useSelector(state => state.billingReducer.upgradePlan.selectedPlanScopesMiniQuantity)
  const scopesLGQuantity = useSelector(state => state.billingReducer.upgradePlan.selectedPlanScopesLGQuantity)
  const scopesLGMiniQuantity = useSelector(state => state.billingReducer.upgradePlan.selectedPlanScopesLGMiniQuantity)
  const { price: scopePrice } = useSelector(state => state.productReducer.scope.data) || {}
  const shippingFee = useSelector(state => state.productReducer.shippingFee)
  const { price: scopeMiniPrice } = useSelector(state => state.productReducer.scopeMini.data) || {}
  const { price: scopeLGPrice } = useSelector(state => state.productReducer.scopeLG.data) || {}
  const { price: scopeLGMiniPrice } = useSelector(state => state.productReducer.scopeLGMini.data) || {}
  const scopesShippingAddress = useSelector(state => state.billingReducer.upgradePlan.selectedPlanScopesShippingAddress)

  const isCalculatingTaxes = useSelector(state => state.billingReducer.order.isLoadingTaxes)
  const taxes = useSelector(state => state.billingReducer.order.taxAmount)
  const gaFlags = useGaFlags()

  const selectedPlan = grinPlans[assemblePlanKey(selectedPlanGroup, selectedPlanPeriod)]

  const currency = '$'
  const monthPrice = useMemo(() => grinPlans[`${selectedPlanGroup}_monthly`]?.price, [grinPlans, selectedPlanGroup])

  const annualPrice = useMemo(() => monthPrice * 12, [monthPrice])
  const hasOrderedScopes = useMemo(
    () => scopesQuantity > 0 || scopesMiniQuantity > 0 || scopesLGQuantity > 0 || scopesLGMiniQuantity > 0,
    [scopesLGMiniQuantity, scopesLGQuantity, scopesMiniQuantity, scopesQuantity]
  )

  const scopePriceTotal = useMemo(() => scopesQuantity * scopePrice || 0, [scopePrice, scopesQuantity])
  const scopeMiniPriceTotal = useMemo(
    () => scopesMiniQuantity * scopeMiniPrice || 0,
    [scopeMiniPrice, scopesMiniQuantity]
  )
  const scopeLGPriceTotal = useMemo(() => scopesLGQuantity * scopeLGPrice || 0, [scopeLGPrice, scopesLGQuantity])
  const scopeLGMiniPriceTotal = useMemo(
    () => scopesLGMiniQuantity * scopeLGMiniPrice || 0,
    [scopeLGMiniPrice, scopesLGMiniQuantity]
  )

  const { coupon } = useSelector(state => state.billingReducer.order.coupon)

  const { getStateKey } = useCountry()
  const { shippingFee: newShippingFee } = useScopesShippingFee(shippingAddress, {
    regular: scopesQuantity,
    mini: scopesMiniQuantity,
    lg: scopesLGQuantity,
    lgMini: scopesLGMiniQuantity
  })
  const isNewShippingFee = useMemo(() => gaFlags?.newShippingFee, [gaFlags])
  const effectiveShippingFee = useMemo(
    () => (isNewShippingFee ? newShippingFee?.price || 0 : shippingFee || 0),
    [isNewShippingFee, newShippingFee, shippingFee]
  )

  const mappedShippingAddress = useMemo(() => {
    const address = {
      street: shippingAddress.address1,
      city: shippingAddress.city,
      country: shippingAddress.country,
      state: getStateKey(shippingAddress.state, shippingAddress.country),
      zip: shippingAddress.zip
    }
    if (shippingAddress.country === COUNTRY_IN) {
      address.detailedAddress = shippingAddress.address2
    }
    return address
  }, [getStateKey, shippingAddress])

  const setInitialShippingAddress = useCallback(() => {
    const initialShippingAddress = scopesShippingAddress?.country ? scopesShippingAddress : practiceDetails

    setShippingAddress({
      address1: initialShippingAddress?.address1,
      address2: initialShippingAddress?.address2,
      city: initialShippingAddress?.city,
      country: initialShippingAddress?.country || COUNTRY_US,
      state: initialShippingAddress?.state,
      zip: initialShippingAddress?.zip
    })
  }, [practiceDetails, scopesShippingAddress])

  const handleClose = useCallback(() => {
    dispatch(Actions.setUpgradePlanDialogVisibility(false))
    dispatch(Actions.setSelectedPlanGroupAndPeriod({ planGroup: null, planPeriod: BILLING_PERIOD_MONTHLY }))
  }, [dispatch])

  const handleUpgradePlan = useCallback(() => {
    if (isSelectedPlanUpgrading) {
      return
    }

    const upgradePayload = {
      plan: selectedPlan,
      shippingAddress: mappedShippingAddress,
      ...(hasOrderedScopes && {
        scopesQuantity: +scopesQuantity,
        ...(scopesMiniQuantity && { scopesMiniQuantity: +scopesMiniQuantity }),
        ...(scopesLGQuantity && { scopesLGQuantity: +scopesLGQuantity }),
        ...(scopesLGMiniQuantity && { scopesLGMiniQuantity: +scopesLGMiniQuantity })
      })
    }

    dispatch(Actions.upgradePlan(upgradePayload))
  }, [
    dispatch,
    hasOrderedScopes,
    isSelectedPlanUpgrading,
    mappedShippingAddress,
    scopesLGMiniQuantity,
    scopesLGQuantity,
    scopesMiniQuantity,
    scopesQuantity,
    selectedPlan
  ])

  const discountItems = useMemo(
    () =>
      coupon?.discountAmount ? [{ description: [coupon.name], price: coupon.discountAmount, isCoupon: true }] : [],
    [coupon]
  )

  const scopeOrderItems = useMemo(
    () =>
      scopesQuantity > 0
        ? [
            {
              description: `${currency}${scopePrice} x ${scopesQuantity} ${t('dialogs.upgradePlan.grinScopes')}`,
              price: scopePriceTotal
            }
          ]
        : [],
    [scopePrice, scopePriceTotal, scopesQuantity, t]
  )

  const scopesMiniOrderItems = useMemo(
    () =>
      scopesMiniQuantity > 0
        ? [
            {
              description: `${currency}${scopeMiniPrice} x ${scopesMiniQuantity} ${t(
                'dialogs.upgradePlan.grinScopesMini'
              )}`,
              price: scopeMiniPriceTotal
            }
          ]
        : [],
    [scopeMiniPrice, scopeMiniPriceTotal, scopesMiniQuantity, t]
  )

  const scopesLGOrderItems = useMemo(
    () =>
      scopesLGQuantity > 0
        ? [
            {
              description: `${currency}${scopeLGPrice} x ${scopesLGQuantity} ${t('dialogs.upgradePlan.grinScopesLG')}`,
              price: scopeLGPriceTotal
            }
          ]
        : [],
    [scopeLGPrice, scopeLGPriceTotal, scopesLGQuantity, t]
  )

  const scopesLGMiniOrderItems = useMemo(
    () =>
      scopesLGMiniQuantity > 0
        ? [
            {
              description: `${currency}${scopeLGMiniPrice} x ${scopesLGMiniQuantity} ${t(
                'dialogs.upgradePlan.grinScopesLGMini'
              )}`,
              price: scopeLGMiniPriceTotal
            }
          ]
        : [],
    [scopeLGMiniPrice, scopeLGMiniPriceTotal, scopesLGMiniQuantity, t]
  )

  const shippingFeeOrderItem = useMemo(
    () =>
      hasOrderedScopes
        ? [
            {
              description: t('dialogs.upgradePlan.shippingFee'),
              price: effectiveShippingFee
            }
          ]
        : [],
    [effectiveShippingFee, hasOrderedScopes, t]
  )

  const monthlyOrderItems = useMemo(
    () => [
      {
        description: `${t('dialogs.upgradePlan.description1', {
          price: `${currency}${divideByThousands(monthPrice)}`,
          activeUsers: selectedPlan?.maxPatients
        })}*`,
        price: monthPrice
      },
      ...scopeOrderItems,
      ...scopesMiniOrderItems,
      ...scopesLGOrderItems,
      ...scopesLGMiniOrderItems,
      ...shippingFeeOrderItem,
      ...discountItems,
      { description: `${t('dialogs.upgradePlan.tax')}**`, price: taxes }
    ],
    [
      t,
      monthPrice,
      selectedPlan,
      scopeOrderItems,
      scopesMiniOrderItems,
      scopesLGOrderItems,
      scopesLGMiniOrderItems,
      shippingFeeOrderItem,
      taxes,
      discountItems
    ]
  )

  const annuallyOrderItems = useMemo(
    () => [
      {
        description: `${t('dialogs.upgradePlan.description2', {
          price: `${currency}${divideByThousands(monthPrice)}`,
          activeUsers: selectedPlan?.maxPatients
        })}`,
        price: annualPrice
      },
      {
        description: t('dialogs.upgradePlan.annualDiscount2'),
        price: -(annualPrice - selectedPlan?.price)
      },
      ...scopeOrderItems,
      ...scopesMiniOrderItems,
      ...scopesLGOrderItems,
      ...scopesLGMiniOrderItems,
      ...shippingFeeOrderItem,
      ...discountItems,
      { description: `${t('dialogs.upgradePlan.tax')}**`, price: taxes }
    ],
    [
      t,
      monthPrice,
      selectedPlan,
      annualPrice,
      scopeOrderItems,
      scopesMiniOrderItems,
      scopesLGOrderItems,
      scopesLGMiniOrderItems,
      shippingFeeOrderItem,
      taxes,
      discountItems
    ]
  )

  useEffect(() => {
    setInitialShippingAddress()
  }, [practiceDetails, setInitialShippingAddress])

  useEffect(() => {
    if (!isOpen || !selectedPlan?.key || !mappedShippingAddress.country) {
      return
    }

    dispatch(Actions.resetCoupon({}))

    dispatch(
      Actions.calculateOrderTax({
        quantity: scopesQuantity,
        quantityMini: scopesMiniQuantity,
        quantityLG: scopesLGQuantity,
        quantityLGMini: scopesLGMiniQuantity,
        shippingAddress: mappedShippingAddress,
        grinPlanKey: selectedPlan.key
      })
    )
  }, [
    dispatch,
    isOpen,
    mappedShippingAddress,
    scopesLGMiniQuantity,
    scopesLGQuantity,
    scopesMiniQuantity,
    scopesQuantity,
    selectedPlan?.key
  ])

  const resetCoupon = useCallback(() => dispatch(Actions.clearCouponCode()), [dispatch])

  return isOpen ? (
    <BaseModal
      open={isOpen}
      title={t('dialogs.upgradePlan.yourPlan', { plan: t(`grinPlans.displayName.${selectedPlan.planGroup}`) })}
      variant={'fullscreen'}
      handleClose={handleClose}
      handleBackdropClick={handleClose}
    >
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <OrderItemsList
            onResetCoupon={resetCoupon}
            hasCoupon={coupon.discountAmount}
            couponFieldWidth={450}
            items={selectedPlanPeriod === BILLING_PERIOD_ANNUALLY ? annuallyOrderItems : monthlyOrderItems}
            isLoading={isCalculatingTaxes}
          />
          <DazzedParagraph12 className={classes.calculationItemNote} strong>
            {`* ${t('dialogs.upgradePlan.disclaimer1', { date: getRenewDate(selectedPlanPeriod) })}`}
          </DazzedParagraph12>
          <DazzedParagraph12 className={classes.calculationItemNote} strong>
            {`** ${t('dialogs.upgradePlan.disclaimer2')}`}
          </DazzedParagraph12>

          <div className={classes.horizontalDivider} />

          <div className={classes.creditCardContainer}>
            <div className={classes.creditCardTitleContainer}>
              <DazzedHeading16 className={classes.creditCardTitle}>
                {t('dialogs.upgradePlan.paymentMethod')}
              </DazzedHeading16>
              <CreditCardPaymentIcons />
            </div>
            <CreditCardPicker
              submitLabel={t('dialogs.upgradePlan.purchaseButton')}
              submitButtonWidth="350px"
              iconButtons={false}
              isSubmitLoading={isSelectedPlanUpgrading}
              showAgreement={true}
              onSubmit={handleUpgradePlan}
              shippingAddress={hasOrderedScopes ? shippingAddress : undefined}
              setAddress={hasOrderedScopes ? setShippingAddress : undefined}
              handleAddressChanged={!hasOrderedScopes ? setShippingAddress : () => {}}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  ) : null
}

export default BillingUpgradePlanDialog
